/**
 * Welle 120 — Positionen: Zeilentypen Position / Gruppe (1.0, 1.1 … mit Zwischensumme) / Freitext,
 * Einzelpreis netto ODER brutto eintragbar, Zeile kopieren (⧉) und verschieben (↑ ↓), bis zu 5 Artikelfotos je
 * Position (📷, Dropbox). Summen rechnen nur echte Positionen.
 */
import React, { useEffect, useMemo, useState } from "react";
import BausteinWahl from "./BausteinWahl";
import { API, zahl, euro, json } from "./hilfen";
import Ico from "./Ico";

const SAETZE = [19, 7, 0, 16];
const r2 = (v) => Math.round(v * 100) / 100;
// Beträge immer mit höchstens 2 Nachkommastellen (getippte Werte werden beim Verlassen des Felds gerundet)
const zweiStellen = (v) => (v === "" || v === null || v === undefined || !/[.,]\d{3,}/.test(String(v)) ? v : r2(zahl(v)));
const istPos = (p) => (p.typ || "position") === "position";

export function nummern(pos) {
  let g = 0, n = 0, frei = 0;
  return pos.map((p) => {
    const typ = p.typ || "position";
    if (typ === "gruppe") { g += 1; n = 0; return `${g}.0`; }
    if (typ === "text") return "";
    if (g) { n += 1; return `${g}.${n}`; }
    frei += 1; return String(frei);
  });
}

const bruttoEingabe = (p) => p.eingabe === "brutto" && p.preis_brutto !== undefined && p.preis_brutto !== null && p.preis_brutto !== "";
export const zeileNetto = (p) => (bruttoEingabe(p)
  ? r2(r2(zahl(p.menge) * zahl(p.preis_brutto)) / (1 + zahl(p.satz) / 100))
  : r2(zahl(p.menge) * zahl(p.preis)));

// wie rechnung.summen(): brutto eingegebene Positionen je Satz vom Bruttobetrag zurückrechnen (2 × 220 € = 440,00 €)
export function summen(positionen) {
  const je = {};
  (positionen || []).filter(istPos).forEach((p) => {
    const s = zahl(p.satz);
    const d = je[s] || (je[s] = { n: 0, b: 0 });
    if (bruttoEingabe(p)) d.b = r2(d.b + r2(zahl(p.menge) * zahl(p.preis_brutto)));
    else d.n = r2(d.n + r2(zahl(p.menge) * zahl(p.preis)));
  });
  const saetze = Object.entries(je).map(([s, d]) => {
    const satz = Number(s), nb = r2(d.b / (1 + satz / 100));
    return { satz, netto: r2(d.n + nb), ust: r2(r2(d.n * satz / 100) + r2(d.b - nb)) };
  }).sort((a, b) => b.satz - a.satz);
  const netto = r2(saetze.reduce((a, x) => a + x.netto, 0));
  const ust = r2(saetze.reduce((a, x) => a + x.ust, 0));
  return { saetze, netto, ust, brutto: r2(netto + ust) };
}

function FotoThumb({ foto, onWeg }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u = null, aus = false;
    fetch(`${API}/datei?pfad=${encodeURIComponent(foto.pfad)}`).then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (b && !aus) { u = URL.createObjectURL(b); setUrl(u); } }).catch(() => {});
    return () => { aus = true; if (u) URL.revokeObjectURL(u); };
  }, [foto.pfad]);
  return (
    <span className="rg-foto" title={foto.name}>
      {url ? <img src={url} alt={foto.name} /> : <span className="rg-foto-leer">…</span>}
      {onWeg && <button type="button" className="rg-foto-weg" title="Foto entfernen" onClick={onWeg}><Ico n="schliessen" gr={10} /></button>}
    </span>
  );
}

export default function PositionenTabelle({ form, setForm, gesperrt, bausteine, ladeBausteine, sicherstellenId, zeige, nachUpload }) {
  const [laedt, setLaedt] = useState(null);
  const pos = useMemo(() => form.positionen || [], [form.positionen]);
  const nr = useMemo(() => nummern(pos), [pos]);
  const satzRegel = form.vorlagen?.satz_regel ?? 19;

  // neue Zeile ans Ende — leere Positionen (die leere Startzeile) fallen dabei weg
  const leer = (p) => istPos(p) && !String(p.text || "").trim() && !zahl(p.preis) && !zahl(p.preis_brutto);
  const anhaengen = (neu) => setForm((f) => ({ ...f, positionen: [...f.positionen.filter((p) => !leer(p)), neu] }));
  const aendern = (i, felder) => setForm((f) => ({ ...f, positionen: f.positionen.map((p, j) => (j === i ? { ...p, ...felder } : p)) }));
  const weg = (i) => setForm((f) => ({ ...f, positionen: f.positionen.filter((_, j) => j !== i) }));
  const kopieren = (i) => setForm((f) => {
    const { az, ...rest } = f.positionen[i];                 // Kopie ohne Arbeitszeit-Verknüpfung (sonst doppelt)
    const neu = [...f.positionen];
    neu.splice(i + 1, 0, { ...JSON.parse(JSON.stringify(rest)), quelle: "manuell" });
    return { ...f, positionen: neu };
  });
  const schieben = (i, d) => setForm((f) => {
    const j = i + d;
    if (j < 0 || j >= f.positionen.length) return f;
    const neu = [...f.positionen];
    [neu[i], neu[j]] = [neu[j], neu[i]];
    return { ...f, positionen: neu };
  });
  const netto = (i, v) => aendern(i, { preis: v, eingabe: "netto", preis_brutto: undefined });
  const brutto = (i, v) => {
    const s = zahl(pos[i].satz);
    aendern(i, { preis_brutto: v, eingabe: "brutto", preis: r2(zahl(v) / (1 + s / 100)) });
  };
  const satz = (i, v) => {
    const p = pos[i];
    aendern(i, p.eingabe === "brutto" ? { satz: v, preis: r2(zahl(p.preis_brutto) / (1 + v / 100)) } : { satz: v });
  };

  const fotosHochladen = async (i, dateien) => {
    if (!dateien?.length) return;
    const frei = 5 - (pos[i].fotos || []).length;
    if (frei <= 0) { zeige(false, "Höchstens 5 Fotos je Position."); return; }
    setLaedt(i);
    try {
      const rid = await sicherstellenId();
      const neu = [];
      for (const d of [...dateien].slice(0, frei)) {
        const fd = new FormData(); fd.append("file", d);
        neu.push(await json(await fetch(`${API}/${rid}/datei?art=foto`, { method: "POST", body: fd })));
      }
      setForm((f) => ({ ...f, positionen: f.positionen.map((p, j) => (j === i ? { ...p, fotos: [...(p.fotos || []), ...neu] } : p)) }));
      nachUpload && nachUpload();
      zeige(true, `${neu.length} Foto(s) an Position ${nr[i] || i + 1}.`);
    } catch (e) { zeige(false, String(e.message || e)); }
    setLaedt(null);
  };

  // Zwischensumme hinter der letzten Zeile einer Gruppe (bis zur nächsten Gruppe bzw. zum Ende)
  const zwischen = {};
  let gi = -1;
  pos.forEach((p, i) => {
    if (p.typ === "gruppe") gi = i;
    const ende = i === pos.length - 1 || pos[i + 1].typ === "gruppe";
    if (gi >= 0 && ende && pos[gi].zwischensumme) zwischen[i] = { gi, s: summen(pos.slice(gi + 1, i + 1)) };
  });

  return (
    <div className="rg-tabelle-rahmen">
      <table className="rg-tabelle rg-pos">
        <thead>
          <tr><th>Pos.</th><th>Leistung</th><th>Menge</th><th>Einheit</th><th>Einzelpreis netto</th><th>Einzelpreis brutto</th>
            <th>USt %</th><th className="r">Betrag netto</th><th className="r">Betrag brutto</th><th /></tr>
        </thead>
        <tbody>
          {pos.map((p, i) => {
            const typ = p.typ || "position";
            const knoepfe = !gesperrt && (
              <td className="rg-pos-knoepfe">
                <button type="button" className="mb-link" title="nach oben" onClick={() => schieben(i, -1)}><Ico n="hoch" /></button>
                <button type="button" className="mb-link" title="nach unten" onClick={() => schieben(i, 1)}><Ico n="runter" /></button>
                <button type="button" className="mb-link" title="Zeile kopieren (darunter einfügen)" onClick={() => kopieren(i)}><Ico n="kopie" /></button>
                {typ === "position" && (
                  <label className="mb-link rg-foto-knopf" title="Artikelfoto(s) hinzufügen (jpg/png, max. 5)">
                    {laedt === i ? "…" : <Ico n="kamera" />}
                    <input type="file" accept="image/png,image/jpeg" multiple hidden onChange={(e) => { const d = Array.from(e.target.files || []); e.target.value = ""; fotosHochladen(i, d); }} />
                  </label>
                )}
                <button type="button" className="mb-link" title="Zeile entfernen" onClick={() => weg(i)}><Ico n="schliessen" /></button>
              </td>
            );
            const zs = zwischen[i];
            const zsZeile = zs && (
              <tr className="rg-zs" key={`zs${i}`}>
                <td /><td colSpan={6}>Zwischensumme {nr[zs.gi]} {pos[zs.gi].text}</td>
                <td className="r">{euro(zs.s.netto)}</td><td className="r"><b>{euro(zs.s.brutto)}</b></td><td />
              </tr>
            );
            if (typ === "gruppe") {
              return (
                <React.Fragment key={i}>
                  <tr className="rg-gruppe">
                    <td className="rg-nr">{nr[i]}</td>
                    <td colSpan={6}>
                      <input className="mb-input rg-gruppe-text" value={p.text || ""} placeholder="Gruppe, z. B. Auslagen Strom" onChange={(e) => aendern(i, { text: e.target.value })} />
                    </td>
                    <td colSpan={2}>
                      <label className="rg-check"><input type="checkbox" checked={!!p.zwischensumme} onChange={(e) => aendern(i, { zwischensumme: e.target.checked })} /> Zwischensumme</label>
                    </td>
                    {knoepfe || <td />}
                  </tr>
                  {zsZeile}
                </React.Fragment>
              );
            }
            if (typ === "text") {
              return (
                <React.Fragment key={i}>
                  <tr className="rg-freitext">
                    <td className="rg-nr" title="Freitext ohne Betrag"><Ico n="absatz" gr={14} /></td>
                    <td colSpan={8}>
                      <textarea className="mb-input rg-area" rows={Math.min(5, Math.max(1, Math.ceil(String(p.text || "").length / 110)))} value={p.text || ""}
                        placeholder="Freitext (ohne Betrag), z. B. Auf Basis des zwischen uns geschlossenen Pachtvertrages …" onChange={(e) => aendern(i, { text: e.target.value })} />
                      {!gesperrt && <BausteinWahl bausteine={bausteine} nurEinfuegen aktuellerText={p.text}
                        onEinfuegen={(t) => aendern(i, { text: p.text ? `${p.text}\n${t}` : t })} onGespeichert={ladeBausteine} />}
                    </td>
                    {knoepfe || <td />}
                  </tr>
                  {zsZeile}
                </React.Fragment>
              );
            }
            const b = p.eingabe === "brutto" ? (p.preis_brutto ?? "") : (p.preis === "" || p.preis == null ? "" : r2(zahl(p.preis) * (1 + zahl(p.satz) / 100)));
            const betrag = zeileNetto(p);
            const betragBrutto = bruttoEingabe(p) ? r2(zahl(p.menge) * zahl(p.preis_brutto)) : r2(betrag * (1 + zahl(p.satz) / 100));
            return (
              <React.Fragment key={i}>
                <tr>
                  <td className="rg-nr">{nr[i]}</td>
                  <td>
                    <textarea className="mb-input rg-area rg-text" rows={Math.min(4, Math.max(1, Math.ceil(String(p.text || "").length / 62)))} value={p.text || ""} onChange={(e) => aendern(i, { text: e.target.value })} />
                    {(p.az || []).length > 0 && <div className="rg-pos-herkunft"><Ico n="uhr" gr={13} /> {p.az.length} Arbeitszeit-Eintrag/Einträge</div>}
                    {(p.fotos || []).length > 0 && (
                      <div className="rg-fotos">
                        {p.fotos.map((f, k) => <FotoThumb key={f.pfad} foto={f} onWeg={gesperrt ? null : () => aendern(i, { fotos: p.fotos.filter((_, x) => x !== k) })} />)}
                      </div>
                    )}
                  </td>
                  <td><input className="mb-input rg-zahl" value={p.menge ?? ""} onChange={(e) => aendern(i, { menge: e.target.value })} /></td>
                  <td><input className="mb-input rg-einheit" value={p.einheit || ""} onChange={(e) => aendern(i, { einheit: e.target.value })} /></td>
                  <td><input className={`mb-input rg-zahl${p.eingabe === "brutto" ? " rg-abgeleitet" : ""}`}
                    value={p.eingabe === "brutto" ? r2(zahl(p.preis)) : (p.preis ?? "")} onChange={(e) => netto(i, e.target.value)}
                    onBlur={(e) => { const v = zweiStellen(e.target.value); if (v !== e.target.value) netto(i, v); }} /></td>
                  <td><input className={`mb-input rg-zahl${p.eingabe !== "brutto" ? " rg-abgeleitet" : ""}`} value={b} onChange={(e) => brutto(i, e.target.value)}
                    onBlur={(e) => { const v = zweiStellen(e.target.value); if (p.eingabe === "brutto" && v !== e.target.value) brutto(i, v); }} /></td>
                  <td>
                    <select className="mb-input rg-satz" value={zahl(p.satz)} onChange={(e) => satz(i, Number(e.target.value))}>
                      {[...new Set([...SAETZE, zahl(p.satz)])].map((x) => <option key={x} value={x}>{x} %</option>)}
                    </select>
                  </td>
                  <td className="r">{euro(betrag)}</td>
                  <td className="r">{euro(betragBrutto)}</td>
                  {knoepfe || <td />}
                </tr>
                {zsZeile}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {!gesperrt && (
        <div className="rg-knoepfe">
          <button type="button" className="mb-btn" onClick={() => setForm((f) => ({ ...f, positionen: [...f.positionen, { text: "", menge: 1, einheit: "", preis: 0, satz: satzRegel }] }))}><Ico n="plus" /> Position</button>
          <button type="button" className="mb-btn" title="Überschrift 1.0, 2.0 … — Positionen darunter werden 1.1, 1.2 …" onClick={() => anhaengen({ typ: "gruppe", text: "", zwischensumme: true })}><Ico n="plus" /> Gruppe</button>
          <button type="button" className="mb-btn" title="Text ohne Betrag zwischen den Positionen" onClick={() => anhaengen({ typ: "text", text: "" })}><Ico n="plus" /> Freitext</button>
        </div>
      )}
    </div>
  );
}
